import React, { CSSProperties } from 'react';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Label from '../Label/Label.js';

import { ILinearProgressBarConfiguration, ILinearProgressBarProps, ILinearProgressBarState } from './models/index.js';
import Bar from './styledComponents/Bar.js';
import Wrapper from './styledComponents/Wrapper.js';
import { LINEAR_PROGRESS_BAR_CATEGORY_KEY, LINEAR_PROGRESS_BAR_COMPONENT_KEY } from './LinearProgressBar.meta.js';

export class LinearProgressBar extends ComponentBase<
	ILinearProgressBarProps,
	ILinearProgressBarState,
	ILinearProgressBarConfiguration
> {
	categoryKey = LINEAR_PROGRESS_BAR_CATEGORY_KEY;
	componentKey = LINEAR_PROGRESS_BAR_COMPONENT_KEY;

	constructor(props: ILinearProgressBarProps) {
		super(props);

		makeObservable(this, {
			stops: observable,
		});

		this.run();
	}

	stops: any = {
		start: 0, // Start of bar
		first: 0, // Start of active segment
		second: 0, // End of active segment
		finish: 100, // End of bar
	};

	run() {
		const { interval = 10, activeSegmentWidth = 10, shading = -1 } = this.props;
		setInterval(() => {
			// Push the active segment forward
			this.stops.second += 1;
			if (this.stops.second >= activeSegmentWidth) {
				this.stops.first += 1;
			}
			// If the active segment has reached the end, loop around
			if (this.stops.first === 100) {
				this.stops.first = 0;
				this.stops.second = 1;
			}
			// Adjust the start and end of the bar to get the right level of shading
			if (shading >= 0) {
				this.stops.start = Math.max(this.stops.first - shading, 0);
				this.stops.finish = Math.min(this.stops.second + shading, 100);
			}
		}, interval);
	}

	render() {
		const { className, backgroundColor = 'rgba(0,212,255,1)', activeColor = 'rgba(9,9,121,1)', text } = this.props;

		const barStyle: CSSProperties = {
			backgroundImage:
				`linear-gradient(90deg,` +
				` ${backgroundColor} ${this.stops.start}%,` +
				` ${activeColor} ${this.stops.first}%,` +
				` ${activeColor} ${this.stops.second}%,` +
				` ${backgroundColor} ${this.stops.finish}%)`,
		};

		return (
			<Wrapper className={className} {...this.props}>
				<Bar style={barStyle} />
				{text && <Label>{text}</Label>}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(LinearProgressBar),
	LINEAR_PROGRESS_BAR_CATEGORY_KEY,
	LINEAR_PROGRESS_BAR_COMPONENT_KEY,
);
